// @flow

import * as React from "react";
import { connect, useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import Config from "../../constants/Config";
import { ReactForm, ReactFormField } from "@adrenalin/react-form";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { login, oktaLogin } from "../../actions/user";
import useAsyncState from "../../hooks/useAsyncState";
import BackButton from "../atoms/BackButton";
import ServerErrorMessage from "../atoms/ServerErrorMessage";
import Button from "../atoms/Button";
import LinkHandler from "../atoms/LinkHandler";
import CustomMeta from "../atoms/CustomMeta";
import Store from "../../store";

const LoginProfileBody = (props): React.Node => {
    const options = Store.getState().options;
    const dispatch = useDispatch();
    const recaptchaRef = React.useRef();
    const [state, localActions] = useAsyncState();
    const [manualLogin, setManualLogin] = React.useState(false);

    const handleFormSubmit = async (formData: any): any => {
        let googleRecaptchaResponse = await recaptchaRef.current.executeAsync();

        localActions.request();
        props
            .login({
                ...formData,
                recaptcha_token: googleRecaptchaResponse,
            })
            .then(res => {
                localActions.success();
            })
            .catch((error: any) => {
                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }

                console.error(error);

                localActions.fail(
                    error.message || "Login email or password is incorrect."
                );
            });
    };

    return (
        <div className="login-profile-body container-fluid">
            <CustomMeta title="Login" />

            <div className="row">
                <div className="col-xs-12 col-md-offset-2 col-md-8">
                    <SwitchTransition>
                        <CSSTransition
                            key={manualLogin ? 1 : 2}
                            timeout={350}
                            classNames="fade"
                            mountOnEnter
                            unmountOnExit
                        >
                            {manualLogin ? (
                                <div className="row">
                                    <div className="col-xs-12 col-md-10 col-lg-6">
                                        <BackButton
                                            className="login-profile-body__back"
                                            onClick={() =>
                                                setManualLogin(false)
                                            }
                                        >
                                            <span className="text">Back</span>
                                        </BackButton>
                                        <h3 className="login-profile-body__content__title">
                                            Login without UTS Email
                                        </h3>
                                        <ServerErrorMessage>
                                            {state.error}
                                        </ServerErrorMessage>
                                        <ReactForm
                                            className={
                                                state.error ? "is-invalid" : ""
                                            }
                                            onSuccess={handleFormSubmit}
                                        >
                                            <ReactFormField
                                                type="email"
                                                label="Email"
                                                name="email"
                                                required={true}
                                                validators={["email"]}
                                            />
                                            <ReactFormField
                                                type="password"
                                                label="Password"
                                                name="password"
                                                required={true}
                                            />

                                            <Button
                                                type="submit"
                                                styles={["green"]}
                                                aria-label="Login"
                                                loading={state.loading}
                                                success={!!state.success}
                                            >
                                                Login
                                            </Button>

                                            <div className="login-profile-body__form__footer">
                                                <ReactFormField
                                                    className="react-form__field--remember"
                                                    type="checkbox"
                                                    label="Remember me"
                                                    name="remember"
                                                />
                                                <div>
                                                    <LinkHandler
                                                        to={
                                                            Config.routes
                                                                .forgotPassword
                                                        }
                                                        className="login-profile-body__form__forget-password"
                                                    >
                                                        <span>
                                                            Forgot Password?
                                                        </span>
                                                    </LinkHandler>
                                                </div>
                                            </div>
                                        </ReactForm>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div>
                                        Have an account or a UTS email? Log in
                                        to access discounts, register for events
                                        and join clubs
                                    </div>
                                    <div className="login-profile-body__cta-container">
                                        <div className="row row--padding-small">
                                            <div className="col-xs-12 col-sm-inline">
                                                <Button
                                                    styles={["green"]}
                                                    aria-label="Login with UTS email"
                                                    onClick={() =>
                                                        dispatch(oktaLogin())
                                                    }
                                                >
                                                    Login with UTS email *
                                                </Button>
                                            </div>
                                            <div className="col-xs-12 col-sm-inline">
                                                <Button
                                                    styles={["green-secondary"]}
                                                    aria-label="I don’t have a UTS email"
                                                    onClick={() =>
                                                        setManualLogin(true)
                                                    }
                                                >
                                                    I don’t have a UTS email
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="row row--padding-small">
                                            <div className="col-xs-12 col-sm-inline">
                                                <div
                                                    className="login-profile-body__cta-footer"
                                                    dangerouslySetInnerHTML={{
                                                        __html: options?.login_disclaimer,
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </CSSTransition>
                    </SwitchTransition>
                </div>
            </div>
            <div
                className={`login-profile-body__recaptcha-container ${
                    manualLogin ? "" : "hidden"
                }`}
            >
                <ReCAPTCHA
                    ref={recaptchaRef}
                    size="invisible"
                    sitekey={Config.recaptchaSiteKey}
                />
            </div>
        </div>
    );
};

const mapDispatchToProps = {
    login: login,
};

export default withRouter(connect(null, mapDispatchToProps)(LoginProfileBody));
